'use client';

import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  animate,
  motion,
  useAnimationControls,
  useInView,
  useReducedMotion,
  type Variants,
} from 'framer-motion';
import { PortableText } from '@portabletext/react';
import { stegaClean } from '@sanity/client/stega';

import { GroqImage } from '@/helpers/Image';
import { measuredRichText } from './richtext';
import type { PagePatrickAffiliateSchema } from '@/sanity/pages/PagePatrickAffiliateTemplate';

import stepsGrid from '@/public/patrick-affiliate/steps-grid.png';
import waveDots from '@/public/fabio-affiliate/wave-dots.png';

type Step = PagePatrickAffiliateSchema['stepsItems'][number];

const ease = [0.25, 0.1, 0.25, 1] as const;
const easeOutExpo = [0.16, 1, 0.3, 1] as const;

const useIsomorphicLayoutEffect =
  typeof window !== 'undefined' ? useLayoutEffect : useEffect;

/**
 * Entrance is framer-motion, driven by one `whileInView` per row and cascaded
 * into the card internals through variant propagation — which is why every
 * wrapper down to a leaf is a `motion` element.
 *
 * Hover is CSS (`group-hover`) and never touches an element whose transform
 * framer already owns: where a hover lift would collide with an entrance
 * transform, the lift goes on a wrapper. Nothing inside a card uses
 * `-translate-*` for centring, for the same reason. The one exception is the
 * progress bars, which replay a `width` animation CSS cannot reach, so the card
 * also tracks hover as state.
 */
const rowVariants: Variants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.14, delayChildren: 0.05 } },
};

const cardVariants: Variants = {
  hidden: { opacity: 0, y: 36, scale: 0.985 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 0.85,
      ease,
      staggerChildren: 0.09,
      delayChildren: 0.22,
    },
  },
};

const fadeUp: Variants = {
  hidden: { opacity: 0, y: 14 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease } },
};

/** The white receipt on step 03 slides up into the card. */
const slideUp: Variants = {
  hidden: { opacity: 0, y: 72 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.95,
      ease: easeOutExpo,
      staggerChildren: 0.09,
      delayChildren: 0.34,
    },
  },
};

/** "Watch it drop." — the discounted price falls in and settles. */
const dropIn: Variants = {
  hidden: { opacity: 0, y: -30, scale: 1.18 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { type: 'spring', stiffness: 420, damping: 22, mass: 0.8 },
  },
};

const popIn: Variants = {
  hidden: { opacity: 0, scale: 0.6 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: { type: 'spring', stiffness: 520, damping: 18, delay: 0.12 },
  },
};

/**
 * Counts hover *starts*, so anything keyed off it replays exactly once per
 * hover and runs to completion — pulling the pointer away immediately must not
 * cut the animation short, which is why none of the replays below stop on
 * unhover. Only unmount stops them.
 */
function useReplayTrigger(hovered: boolean) {
  const [count, setCount] = useState(0);
  const previous = useRef(false);

  useEffect(() => {
    if (hovered && !previous.current) setCount((c) => c + 1);
    previous.current = hovered;
  }, [hovered]);

  return count;
}

/** Sweeps a soft highlight across the card, once per hover. */
function Sheen({ replay }: { replay: number }) {
  const reduceMotion = useReducedMotion();
  const controls = useAnimationControls();

  useEffect(() => {
    if (!replay || reduceMotion) return;
    controls.set({ x: '-180%' });
    controls.start({ x: '350%' }, { duration: 1.1, ease: 'easeOut' });
  }, [replay, reduceMotion, controls]);

  return (
    <div className="overflow-hidden absolute inset-0 rounded-[0.75rem] pointer-events-none">
      {/* skewX rides along in framer's transform; a `-skew-x-12` class would be
          overwritten the moment framer animates `x`. */}
      <motion.div
        initial={{ x: '-180%', skewX: -12 }}
        animate={controls}
        className="absolute inset-y-0 bg-gradient-to-r from-transparent to-transparent w-[40%] via-white/[0.14]"
      />
    </div>
  );
}

// ── Progress bars ───────────────────────────────────────────────────────────

const BAR_ENTRANCE = { duration: 1.25, ease: easeOutExpo, delay: 0.2 } as const;
/** The hover replay, a touch quicker than the reveal so it feels deliberate. */
const BAR_REPLAY = { duration: 0.9, ease: easeOutExpo } as const;

/** One account bar: fills on reveal, and refills from zero on every hover. */
function ProgressBar({
  percent,
  highlight,
  replay,
}: {
  percent: number;
  highlight?: boolean;
  replay: number;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, amount: 0.3 });
  const reduceMotion = useReducedMotion();
  const controls = useAnimationControls();

  const width = `${percent}%`;

  // Reveal.
  useEffect(() => {
    if (reduceMotion) {
      controls.set({ width });
      return;
    }
    if (inView) controls.start({ width }, BAR_ENTRANCE);
  }, [inView, reduceMotion, controls, width]);

  // Hover: snap back to empty, then run the fill again. Nothing here stops the
  // animation, so it finishes even if the pointer has already left.
  useEffect(() => {
    if (!replay || !inView || reduceMotion) return;
    controls.set({ width: '0%' });
    controls.start({ width }, BAR_REPLAY);
  }, [replay, inView, reduceMotion, controls, width]);

  return (
    <div
      ref={ref}
      className={`flex overflow-clip w-full rounded-full ${
        highlight ? 'h-[0.75rem] bg-black/10' : 'h-[0.5rem] bg-white/[18%]'
      }`}
    >
      <motion.div
        initial={{ width: '0%' }}
        animate={controls}
        className={`relative h-full rounded-full ${
          highlight ? 'bg-[#228E71]' : 'bg-white/50'
        }`}
      >
        {highlight && (
          <span className="absolute inset-0 rounded-full bg-[#228E71]/50 blur-[0.25rem] opacity-0 transition-opacity duration-500 group-hover:opacity-70" />
        )}
      </motion.div>
    </div>
  );
}

// ── Number counting ─────────────────────────────────────────────────────────

type NumericSpec = {
  prefix: string;
  suffix: string;
  amount: number;
  /** Thousands separator to render back, '' when the value has none. */
  sep: string;
  decimals: number;
};

/**
 * Reads a money string into something animatable. Handles both grouping
 * conventions the design uses — "$100,000" and "5.000 $" — which a naive
 * parser reads as 100 and 5.
 */
function parseNumeric(raw?: string): NumericSpec | null {
  const clean = stegaClean(raw) ?? '';
  const match = clean.match(/^(\D*?)([\d.,]+)(\D*)$/);
  if (!match) return null;
  const [, prefix, digits, suffix] = match;

  const grouped = digits.match(/^\d{1,3}(?:([.,])\d{3})+$/);
  if (grouped) {
    const sep = grouped[1];
    const amount = Number(digits.split(sep).join(''));
    return Number.isFinite(amount)
      ? { prefix, suffix, amount, sep, decimals: 0 }
      : null;
  }

  const dot = digits.lastIndexOf('.');
  const amount = Number(digits.replace(/,/g, ''));
  if (!Number.isFinite(amount)) return null;
  return {
    prefix,
    suffix,
    amount,
    sep: digits.includes(',') ? ',' : '',
    decimals: dot >= 0 ? digits.length - dot - 1 : 0,
  };
}

function formatNumeric(spec: NumericSpec, n: number) {
  const fixed = Math.abs(n).toFixed(spec.decimals);
  const [int, frac] = fixed.split('.');
  const grouped = spec.sep
    ? int.replace(/\B(?=(\d{3})+(?!\d))/g, spec.sep)
    : int;
  return `${spec.prefix}${grouped}${frac ? `.${frac}` : ''}${spec.suffix}`;
}

/**
 * Counts to `value` when scrolled into view, and again on every hover. With
 * `from` it counts *down* from that value instead, which is what step 02's
 * price drop uses.
 */
function CountUp({
  value,
  from,
  delay = 0,
  duration = 1.6,
  replay = 0,
  className,
}: {
  value?: string;
  from?: string;
  delay?: number;
  duration?: number;
  replay?: number;
  className?: string;
}) {
  const ref = useRef<HTMLSpanElement>(null);
  const inView = useInView(ref, { once: true, amount: 0.3 });
  const reduceMotion = useReducedMotion();

  const spec = useMemo(() => parseNumeric(value), [value]);
  const start = useMemo(() => parseNumeric(from)?.amount, [from]);
  const clean = stegaClean(value) ?? '';

  // Server-render the real figure so crawlers and a no-JS visitor see
  // "$100,000" rather than "$0"; the client drops it to the start value below
  // before the first paint, so the count is never seen already finished.
  const [display, setDisplay] = useState(clean);
  const active = useRef<{ stop: () => void } | null>(null);

  useIsomorphicLayoutEffect(() => {
    if (spec && !reduceMotion) setDisplay(formatNumeric(spec, start ?? 0));
  }, [spec, start, reduceMotion]);

  // The only place a count is cancelled: leaving the page, never unhovering.
  useEffect(() => () => active.current?.stop(), []);

  // Reveal.
  useEffect(() => {
    if (!spec || reduceMotion) {
      setDisplay(clean);
      return;
    }
    if (!inView) return;
    active.current?.stop();
    active.current = animate(start ?? 0, spec.amount, {
      duration,
      delay,
      ease: easeOutExpo,
      onUpdate: (latest) => setDisplay(formatNumeric(spec, latest)),
    });
  }, [inView, spec, start, delay, duration, reduceMotion, clean]);

  // Hover replay, no entrance delay this time.
  useEffect(() => {
    if (!replay || !spec || reduceMotion) return;
    active.current?.stop();
    active.current = animate(start ?? 0, spec.amount, {
      duration,
      ease: easeOutExpo,
      onUpdate: (latest) => setDisplay(formatNumeric(spec, latest)),
    });
  }, [replay, spec, start, duration, reduceMotion]);

  return (
    <span ref={ref} className={className}>
      {display}
    </span>
  );
}

// ── Card backgrounds ────────────────────────────────────────────────────────

/** Card backgrounds, one per variant — Figma 8741:32598. */
function CardBackground({ step }: { step: Step }) {
  const variant = stegaClean(step?.variant);

  if (variant === 'account') {
    return (
      <>
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage:
              'linear-gradient(143.5deg, #1F8167 13.87%, #2A8C72 86.86%)',
          }}
        />
        <div
          className="absolute inset-0 opacity-20 transition-opacity duration-700 pointer-events-none group-hover:opacity-[0.32]"
          style={{
            backgroundImage: `url(${stepsGrid.src})`,
            backgroundSize: '1.25rem 1.25rem',
            backgroundPosition: 'top left',
          }}
        />
      </>
    );
  }

  const isPayout = variant === 'payout';

  return (
    <>
      <div
        className={`absolute inset-0 pointer-events-none ${isPayout ? 'bg-[#003828]' : 'bg-[#122823]'}`}
      />
      {step?.image && (
        <GroqImage
          src={step.image}
          alt=""
          quality={100}
          // Opts out of the helper turning the asset's pixel width into style.width.
          style={{ width: '100%' }}
          className={`absolute inset-0 object-cover !w-full !h-full pointer-events-none transition-transform ease-out duration-[1200ms] motion-safe:group-hover:scale-[1.07] ${isPayout ? 'opacity-20' : 'opacity-50'}`}
        />
      )}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: isPayout
            ? 'linear-gradient(180deg, rgba(0,56,40,0.86) 0%, rgba(0,27,20,0.7) 100%)'
            : 'linear-gradient(180deg, rgba(0,27,20,0.82) 0%, rgba(0,27,20,0.5) 34%)',
        }}
      />
      {/* The design stacks a luminosity photo and a soft-light noise layer here;
          both read as this dot texture once composited. */}
      {isPayout && (
        <div
          className="absolute inset-0 opacity-10 pointer-events-none"
          style={{
            backgroundImage: `url(${waveDots.src})`,
            backgroundSize: 'cover',
          }}
        />
      )}
    </>
  );
}

// ── Per-variant visuals ─────────────────────────────────────────────────────

/** Step 01 — account sizes counting up as their bars fill. */
function AccountVisual({
  step,
  replay,
}: {
  step: Step;
  replay: number;
}) {
  const rows = step?.account?.rows;
  if (!rows?.length) return null;

  return (
    <motion.div className="flex flex-col items-start w-full gap-[1.25rem]">
      {rows.map((row, i) => {
        const highlight = !!row?.highlight;
        return (
          <motion.div
            key={`${row?.tag ?? 'row'}-${i}`}
            variants={fadeUp}
            className={`flex flex-col justify-center items-start w-full border-t border-white/[18%] rounded-[0.5rem] gap-[1.25rem] p-[2.5rem] ${
              highlight ? 'bg-white h-[15.375rem]' : 'bg-white/10 h-[11.5rem]'
            }`}
          >
            <div className="flex justify-between items-baseline w-full whitespace-nowrap">
              {row?.amount && (
                <p
                  className={`m-0 font-geist font-[500] tabular-nums ${
                    highlight
                      ? 'text-black text-[4.5rem] tracking-[-0.1575rem]'
                      : 'text-white/[62%] text-[3rem] tracking-[-0.105rem]'
                  }`}
                >
                  <CountUp
                    value={row.amount}
                    delay={0.2 + i * 0.12}
                    duration={1.5}
                    replay={replay}
                  />
                </p>
              )}
              {row?.tag && (
                <motion.p
                  variants={fadeUp}
                  className={`m-0 font-geist font-[400] text-[1.75rem] ${
                    highlight ? 'text-[#228E71]' : 'text-white/40'
                  }`}
                >
                  {row.tag}
                </motion.p>
              )}
            </div>
            <ProgressBar
              percent={row?.fillPercent ?? 0}
              highlight={highlight}
              replay={replay}
            />
          </motion.div>
        );
      })}
    </motion.div>
  );
}

/** Step 02 — the price counting down from full price to the coupon price. */
function CouponVisual({
  step,
  replay,
}: {
  step: Step;
  replay: number;
}) {
  const coupon = step?.coupon;
  if (!coupon) return null;

  return (
    <motion.div
      variants={fadeUp}
      className="flex flex-col justify-center items-center w-full whitespace-nowrap bg-black/[13%] rounded-[0.5rem] gap-[1rem] h-[26rem] lg:h-[27.5rem] transition-colors duration-500 group-hover:bg-black/[0.2]"
    >
      {coupon?.priceOriginal && (
        <motion.p
          variants={fadeUp}
          className="m-0 line-through font-geist font-[400] text-white/50 text-[2.5rem]"
        >
          {coupon.priceOriginal}
        </motion.p>
      )}
      {coupon?.priceCurrent && (
        <motion.div className="transition-transform duration-500 ease-out motion-safe:group-hover:-translate-y-[0.5rem] motion-safe:group-hover:scale-[1.05]">
          <motion.p
            variants={dropIn}
            className="m-0 font-geist font-[500] text-[#CFFFDB] leading-[0.92] tabular-nums text-[7.5rem] tracking-[-0.375rem] lg:text-[8.75rem] lg:tracking-[-0.4375rem]"
          >
            <CountUp
              value={coupon.priceCurrent}
              from={coupon.priceOriginal}
              delay={0.35}
              duration={1.5}
              replay={replay}
            />
          </motion.p>
        </motion.div>
      )}
      {coupon?.code && (
        <motion.div
          variants={popIn}
          className="flex items-center border border-dashed rounded-[1rem] gap-[2rem] px-[2.5rem] py-[1.75rem] text-[1.75rem] tracking-[0.245rem] border-white/40 transition-colors duration-500 group-hover:border-white/70 group-hover:bg-white/[0.06]"
        >
          {coupon?.codeLabel && (
            <p className="m-0 font-geist-mono font-[400] text-white/[62%]">
              {coupon.codeLabel}
            </p>
          )}
          <p className="m-0 font-geist-mono font-[500] text-white">
            {coupon.code}
          </p>
        </motion.div>
      )}
    </motion.div>
  );
}

/** Step 03 — the payout receipt sliding up, then filling itself in. */
function PayoutVisual({
  step,
  replay,
}: {
  step: Step;
  replay: number;
}) {
  const payout = step?.payout;
  if (!payout) return null;

  return (
    <motion.div className="w-full transition-transform duration-500 ease-out motion-safe:group-hover:-translate-y-[0.5rem]">
      <motion.div
        variants={slideUp}
        className="flex flex-col items-start w-full bg-white rounded-[1rem] gap-[1.5rem] p-[2.5rem] shadow-[0_0_0_rgba(0,0,0,0)] transition-shadow duration-500 group-hover:shadow-[0_1.5rem_4rem_rgba(0,27,20,0.28)]"
      >
        <motion.div
          variants={fadeUp}
          className="flex justify-between items-center w-full"
        >
          {payout?.headerLabel && (
            <p className="m-0 whitespace-nowrap font-geist-mono font-[400] text-[#001B14]/[42%] text-[1.75rem] tracking-[0.035rem]">
              {payout.headerLabel}
            </p>
          )}
          {payout?.statusLabel && (
            <motion.div className="transition-transform duration-500 motion-safe:group-hover:scale-[1.06]">
              <motion.div
                variants={popIn}
                className="flex items-center rounded-full bg-[#1B8869]/[12%] gap-[0.75rem] px-[1.5rem] py-[0.75rem]"
              >
                <span className="rounded-full shrink-0 bg-[#1B8869] w-[0.75rem] h-[0.75rem] motion-safe:animate-pulse" />
                <p className="m-0 whitespace-nowrap font-geist font-[500] text-[#003828] text-[1.75rem]">
                  {payout.statusLabel}
                </p>
              </motion.div>
            </motion.div>
          )}
        </motion.div>
        {payout?.amount && (
          <motion.p
            variants={fadeUp}
            className="m-0 whitespace-nowrap font-geist font-[500] text-[#001B14] text-[4rem] tracking-[-0.16rem] tabular-nums"
          >
            <CountUp
              value={payout.amount}
              delay={0.5}
              duration={1.7}
              replay={replay}
            />
          </motion.p>
        )}
        {!!payout?.rows?.length && (
          <motion.div className="flex flex-col items-start w-full gap-[1rem] text-[1.75rem]">
            {payout.rows!.map((row, i) => (
              <motion.div
                key={`${row?.label ?? 'row'}-${i}`}
                variants={fadeUp}
                className="flex justify-between items-start w-full border-t border-[#001B14]/[7%] py-[1.25rem]"
              >
                <p className="m-0 font-geist font-[400] text-[#001B14]/50">
                  {row?.label}
                </p>
                <p className="m-0 font-geist font-[500] text-[#001B14]">
                  {row?.value}
                </p>
              </motion.div>
            ))}
          </motion.div>
        )}
      </motion.div>
    </motion.div>
  );
}

// ── Card + section ──────────────────────────────────────────────────────────

/** Splits a textarea value into its rendered lines. */
function lines(value?: string) {
  return (stegaClean(value) ?? '')
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);
}

function StepCard({ step }: { step: Step }) {
  const variant = stegaClean(step?.variant);
  const titleLines = lines(step?.title);
  // The bars and counters replay on hover, which CSS `group-hover` cannot
  // drive, so the card's hover has to be state. Pointer-driven, like `:hover`.
  const [hovered, setHovered] = useState(false);
  const replay = useReplayTrigger(hovered);

  return (
    <motion.div
      variants={cardVariants}
      onHoverStart={() => setHovered(true)}
      onHoverEnd={() => setHovered(false)}
      className="flex flex-col flex-1 items-start w-full gap-[3rem] group"
    >
      {/* The hover lift lives here so it never fights the card's entrance transform. */}
      <motion.div className="w-full transition-transform duration-500 ease-out motion-safe:group-hover:-translate-y-[0.75rem]">
        <motion.div className="overflow-clip relative w-full rounded-[0.75rem] h-[47.75rem] lg:h-[60.5rem] transition-shadow duration-500 group-hover:shadow-[0_2rem_5rem_rgba(0,27,20,0.22)]">
          <CardBackground step={step} />
          <Sheen replay={replay} />

          {/* Flex centring, not a translate — framer animates transforms in here. */}
          <motion.div className="flex absolute inset-0 justify-center items-center p-[2rem]">
            <motion.div
              className={`flex flex-col items-center max-w-full ${
                variant === 'coupon' ? 'w-[37rem]' : 'w-[41rem]'
              } gap-[2.125rem]`}
            >
              <motion.div
                variants={fadeUp}
                className="flex flex-col items-center text-center gap-[0.5rem]"
              >
                {(step?.number || step?.label) && (
                  <div className="flex items-center whitespace-nowrap font-geist font-[400] text-white/[62%] text-[1.75rem] gap-[0.75rem]">
                    {step?.number && <p className="m-0">{step.number}</p>}
                    {step?.label && <p className="m-0">{step.label}</p>}
                  </div>
                )}
                {titleLines.length > 0 && (
                  <p className="m-0 font-geist font-[500] text-white text-[3rem] leading-[1.2] tracking-[-0.09rem]">
                    {titleLines.map((line, i) => (
                      <span key={`${line}-${i}`} className="block">
                        {line}
                      </span>
                    ))}
                  </p>
                )}
              </motion.div>

              {variant === 'account' && (
                <AccountVisual step={step} replay={replay} />
              )}
              {variant === 'coupon' && (
                <CouponVisual step={step} replay={replay} />
              )}
              {variant === 'payout' && (
                <PayoutVisual step={step} replay={replay} />
              )}
            </motion.div>
          </motion.div>
        </motion.div>
      </motion.div>

      {(step?.captionTitle || step?.captionText) && (
        <motion.div
          variants={fadeUp}
          className="flex flex-col items-start w-full gap-[1.25rem]"
        >
          {step?.captionTitle && (
            <p className="m-0 w-full font-geist font-[500] text-[#001B14] text-[2.75rem] leading-[1.2] tracking-[-0.0825rem]">
              {step.captionTitle}
            </p>
          )}
          {step?.captionText && (
            <p className="m-0 font-geist font-[400] text-[#001B14]/60 text-[2.25rem] leading-[1.5] tracking-[-0.0225rem] lg:w-[43rem]">
              {step.captionText}
            </p>
          )}
        </motion.div>
      )}
    </motion.div>
  );
}

export default function StepsSection({
  data,
}: {
  data: PagePatrickAffiliateSchema;
}) {
  const reduceMotion = useReducedMotion();

  if (
    !data?.stepsChip &&
    !data?.stepsHeading?.length &&
    !data?.stepsItems?.length
  )
    return null;

  // Reduced motion: everything mounts in its final state.
  const reveal = reduceMotion
    ? { initial: 'visible' as const, animate: 'visible' as const }
    : {
        initial: 'hidden' as const,
        whileInView: 'visible' as const,
        viewport: { once: true, amount: 0.2 },
      };

  return (
    <section className="bg-white px-[2rem] py-[8rem] lg:px-0 lg:py-[14rem]">
      <div className="flex flex-col mx-auto w-[min(147rem,100%)]">
        <motion.div
          variants={rowVariants}
          {...reveal}
          className="flex flex-col items-center w-full gap-[2rem]"
        >
          {data?.stepsChip && (
            <motion.p
              variants={fadeUp}
              className="m-0 italic text-center text-black font-blacker text-[2rem] leading-[1.5]"
            >
              {data.stepsChip}
            </motion.p>
          )}
          {data?.stepsHeading?.length > 0 && (
            <motion.div
              variants={fadeUp}
              className="richtext richtext-center max-w-full w-[38rem] lg:w-[68.25rem] prose-headings:text-[#001B14] prose-headings:font-geist prose-headings:font-[500] prose-headings:leading-[1.08] prose-headings:text-[4.5rem] prose-headings:tracking-[-0.225rem] lg:prose-headings:text-[8rem] lg:prose-headings:tracking-[-0.4rem] lg:[--hl-dy:-0.21rem]"
            >
              <PortableText
                value={data.stepsHeading}
                components={measuredRichText}
              />
            </motion.div>
          )}
        </motion.div>

        {data?.stepsItems?.length > 0 && (
          <motion.div
            variants={rowVariants}
            {...reveal}
            className="flex flex-col items-start w-full gap-[3rem] pt-[4rem] lg:flex-row"
          >
            {data.stepsItems.map((step, i) => (
              <StepCard
                key={`${step?.captionTitle ?? 'step'}-${i}`}
                step={step}
              />
            ))}
          </motion.div>
        )}
      </div>
    </section>
  );
}
